import { useCallback, useEffect, useRef, useState } from 'react';

import {
    CONFIRM_REPORTER_EMAIL_VERIFICATION,
    ConfirmReporterEmailVerificationData,
    ConfirmReporterEmailVerificationVariables,
    REQUEST_REPORTER_EMAIL_VERIFICATION,
    RequestReporterEmailVerificationData,
    RequestReporterEmailVerificationVariables,
} from './reporterEmailVerification.operations';

import { normaliseEmail } from '@utils/reporterEmail';

import { useMutation, useReactiveVar } from '@apollo/client';
import { verifiedReporterEmailsVar } from '@boot/reactiveVariables';

export const VERIFICATION_CODE_LENGTH = 6;

/**
 * What the reporter is currently being asked to do.
 * `idle` - no code requested yet; `awaitingCode` - a code is outstanding.
 */
export type ReporterEmailVerificationStatus =
    | 'awaitingCode'
    | 'idle'
    | 'verified';

/**
 * Why the last action did not succeed. Kept separate from `status` so a failed
 * attempt does not throw away the fact that a code is still outstanding.
 */
export type ReporterEmailVerificationProblem =
    | 'attemptsExhausted'
    | 'expired'
    /** Nothing left to try, and we cannot tell whether it expired or ran out. */
    | 'noTriesLeft'
    | 'rateLimited'
    | 'unavailable'
    | 'wrongCode';

const asSeconds = (value: number) => Math.max(0, Math.ceil(value));

/**
 * Drives the six-digit confirmation for one reporter email address.
 *
 * An unconfirmed address holds up the report in both modes (decided
 * 2026-08-08). Where the address is optional the reporter either confirms it
 * or removes it; where it is mandatory there is no removal exit, so a
 * reporter whose mailbox cannot receive the code cannot file - the accepted
 * price of a mandatory identity always being a proven one.
 */
export const useReporterEmailVerification = (email?: string | null) => {
    const address = normaliseEmail(email);
    const verifiedEmails = useReactiveVar(verifiedReporterEmailsVar);
    const isVerified = !!address && verifiedEmails.includes(address);

    const [hasOutstandingCode, setHasOutstandingCode] = useState(false);
    const [problem, setProblem] =
        useState<ReporterEmailVerificationProblem | null>(null);
    const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
        null,
    );
    const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const expiresAtRef = useRef<number | null>(null);

    /**
     * The address every in-flight call was made for. A reporter can edit the field
     * while a request is in the air, and a response that lands afterwards describes the
     * address it was sent for, not the one now on screen - applying it would name the
     * new address in "we've sent a code to ..." when no code went there.
     */
    const inFlightForRef = useRef<string | null>(null);

    /**
     * Set synchronously, unlike `loading` from `useMutation`, which is what makes this
     * an actual double-submit guard: two fast clicks would otherwise both get through
     * and the second would come back rate-limited right after a code really was sent.
     */
    const isRequestingRef = useRef(false);

    // A change of address invalidates everything held about the previous one.
    useEffect(() => {
        setHasOutstandingCode(false);
        setProblem(null);
        setAttemptsRemaining(null);
        setCooldownUntil(null);
        expiresAtRef.current = null;
        inFlightForRef.current = null;
    }, [address]);

    // Counted off a deadline rather than decremented, so a backgrounded tab or a
    // throttled timer cannot leave the reporter waiting longer than the server asked.
    useEffect(() => {
        if (cooldownUntil === null) {
            setCooldownSeconds(0);

            return;
        }

        const tick = () => {
            const remaining = asSeconds((cooldownUntil - Date.now()) / 1000);

            setCooldownSeconds(remaining);

            // "You can ask for one again in 0s" is worse than saying nothing - the wait
            // it describes is over, so the message retires with it.
            if (remaining <= 0) {
                setProblem((current) =>
                    current === 'rateLimited' ? null : current,
                );
            }
        };

        tick();

        const timer = window.setInterval(tick, 1000);

        return () => window.clearInterval(timer);
    }, [cooldownUntil]);

    const markVerified = useCallback(() => {
        if (!address) return;

        const current = verifiedReporterEmailsVar();

        if (!current.includes(address)) {
            verifiedReporterEmailsVar([...current, address]);
        }
    }, [address]);

    const [runRequest, { loading: requesting }] = useMutation<
        RequestReporterEmailVerificationData,
        RequestReporterEmailVerificationVariables
    >(REQUEST_REPORTER_EMAIL_VERIFICATION, {
        // The reporting flow must survive a verification outage, so failures are
        // surfaced in-place instead of raising the global error toast.
        onError: () => setProblem('unavailable'),
    });

    const [runConfirm, { loading: confirming }] = useMutation<
        ConfirmReporterEmailVerificationData,
        ConfirmReporterEmailVerificationVariables
    >(CONFIRM_REPORTER_EMAIL_VERIFICATION, {
        onError: () => setProblem('unavailable'),
    });

    const requestCode = useCallback(async () => {
        if (!address || isRequestingRef.current) return;

        isRequestingRef.current = true;
        inFlightForRef.current = address;
        setProblem(null);

        let data;

        try {
            ({ data } = await runRequest({
                variables: { input: { email: address } },
            }));
        } finally {
            isRequestingRef.current = false;
        }

        // The reporter edited the address while this was in the air; the answer is
        // about an address that is no longer on screen.
        if (!data || inFlightForRef.current !== address) return;

        const { cooldownSeconds: cooldown, expiresAt, sent } = data.request;
        const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : null;

        if (!sent) {
            /**
             * The exact signature of the already-confirmed branch: no wait to serve and
             * no code outstanding. Matching on the zero alone would fail open - any
             * future refusal that left `cooldownSeconds` at its default would announce
             * an address as confirmed when nothing had confirmed it.
             */
            if (cooldown <= 0 && expiresAtMs === null) {
                markVerified();

                return;
            }

            // A refusal can still carry a live code from an earlier send. Withholding
            // the code field here would strand a reporter who has the code in their
            // inbox and no way to type it.
            if (expiresAtMs !== null && expiresAtMs > Date.now()) {
                expiresAtRef.current = expiresAtMs;
                setHasOutstandingCode(true);
            }

            setCooldownUntil(Date.now() + asSeconds(cooldown) * 1000);
            setProblem('rateLimited');

            return;
        }

        expiresAtRef.current = expiresAtMs;
        setHasOutstandingCode(true);
        setAttemptsRemaining(null);
        setCooldownUntil(Date.now() + asSeconds(cooldown) * 1000);
    }, [address, markVerified, runRequest]);

    const confirmCode = useCallback(
        async (code: string) => {
            if (!address || code.length !== VERIFICATION_CODE_LENGTH) return;

            inFlightForRef.current = address;
            setProblem(null);

            const { data } = await runConfirm({
                variables: { input: { code, email: address } },
            });

            // The address changed under this call - the verdict is about the old one.
            if (!data || inFlightForRef.current !== address) return;

            const { attemptsRemaining: remaining, verified } = data.confirm;

            if (verified) {
                setHasOutstandingCode(false);
                markVerified();

                return;
            }

            setAttemptsRemaining(remaining);

            if (remaining > 0) {
                setProblem('wrongCode');

                return;
            }

            /**
             * The backend reports "nothing left to try" the same way whether the code
             * expired or the guesses ran out, so the outstanding code's own expiry is
             * what tells the reporter which of the two happened. When we never saw an
             * expiry - the page was reloaded after the code was sent - we say neither
             * rather than guessing; both roads lead to the same next step anyway.
             */
            const expiresAt = expiresAtRef.current;

            if (expiresAt === null) {
                setProblem('noTriesLeft');

                return;
            }

            setProblem(
                expiresAt <= Date.now() ? 'expired' : 'attemptsExhausted',
            );
        },
        [address, markVerified, runConfirm],
    );

    const status: ReporterEmailVerificationStatus = isVerified
        ? 'verified'
        : hasOutstandingCode
          ? 'awaitingCode'
          : 'idle';

    return {
        attemptsRemaining,
        canResend: cooldownSeconds <= 0 && !requesting,
        confirmCode,
        confirming,
        cooldownSeconds,
        isVerified,
        problem,
        requestCode,
        requesting,
        status,
    };
};
